import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { LanguageService, UILanguage } from './language.service';
import { ProductCatalogService, PublicCategory, PublicProduct } from './product-catalog.service';

type DetailDictionary = {
  back: { en: string; nl: string; fr: string };
  contactPricing: { en: string; nl: string; fr: string };
  categories: { en: string; nl: string; fr: string };
  productVideo: { en: string; nl: string; fr: string };
  notFound: { en: string; nl: string; fr: string };
  loading: { en: string; nl: string; fr: string };
};

const DICTIONARY: DetailDictionary = {
  back: {
    en: 'Back to Products',
    nl: 'Terug naar Producten',
    fr: 'Retour aux Produits',
  },
  contactPricing: {
    en: 'Contact Us for Pricing',
    nl: 'Contacteer ons voor prijzen',
    fr: 'Contactez-nous pour les prix',
  },
  categories: {
    en: 'Categories',
    nl: 'Categorieen',
    fr: 'Categories',
  },
  productVideo: {
    en: 'Product Video',
    nl: 'Productvideo',
    fr: 'Video du Produit',
  },
  notFound: {
    en: 'Product not found.',
    nl: 'Product niet gevonden.',
    fr: 'Produit introuvable.',
  },
  loading: {
    en: 'Loading product details...',
    nl: 'Productdetails laden...',
    fr: 'Chargement des details du produit...',
  },
};

@Component({
  selector: 'app-product-details',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './product-details.component.html',
  styleUrl: './product-details.component.css',
})
export class ProductDetailsComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly catalogService = inject(ProductCatalogService);
  private readonly languageService = inject(LanguageService);

  public readonly language = this.languageService.language;
  public readonly loading = this.catalogService.loading;
  public readonly phoneHref = '+32470845847';
  public readonly currentImageIndex = signal<number>(0);

  private readonly productID = signal<string>('');

  public readonly product = computed<PublicProduct | undefined>(() =>
    this.catalogService.getByID(this.productID()),
  );

  public readonly imageURLs = computed<string[]>(() => {
    const product = this.product();
    if (!product) {
      return [];
    }

    return [
      product.product_image_link_1,
      product.product_image_link_2,
      product.product_image_link_3,
      product.product_image_link_4,
      product.product_image_link_5,
    ].filter((item) => item && item.trim() !== '');
  });

  constructor() {
    const id = this.route.snapshot.paramMap.get('id') || '';
    this.productID.set(id);
    void this.catalogService.load();
  }

  public t(key: keyof DetailDictionary): string {
    return DICTIONARY[key][this.language()];
  }

  public productName(product: PublicProduct): string {
    return this.pickLocalizedValue(product.name_english, product.name_dutch, product.name_french);
  }

  public productDescription(product: PublicProduct): string {
    return this.pickLocalizedValue(
      product.description_english,
      product.description_dutch,
      product.description_french,
    );
  }

  public categoryName(category: PublicCategory): string {
    return this.pickLocalizedValue(category.name_english, category.name_dutch, category.name_french);
  }

  public activeImageURL(): string {
    const images = this.imageURLs();
    if (!images.length) {
      return '';
    }
    return images[this.safeImageIndex(this.currentImageIndex(), images.length)];
  }

  public hasMultipleImages(): boolean {
    return this.imageURLs().length > 1;
  }

  public selectImage(index: number): void {
    const images = this.imageURLs();
    if (!images.length) {
      this.currentImageIndex.set(0);
      return;
    }
    this.currentImageIndex.set(this.safeImageIndex(index, images.length));
  }

  public showPreviousImage(): void {
    const images = this.imageURLs();
    const count = images.length;
    if (count <= 1) {
      return;
    }
    const current = this.safeImageIndex(this.currentImageIndex(), count);
    this.currentImageIndex.set((current - 1 + count) % count);
  }

  public showNextImage(): void {
    const images = this.imageURLs();
    const count = images.length;
    if (count <= 1) {
      return;
    }
    const current = this.safeImageIndex(this.currentImageIndex(), count);
    this.currentImageIndex.set((current + 1) % count);
  }

  private safeImageIndex(index: number, total: number): number {
    if (total <= 0) {
      return 0;
    }
    if (index < 0) {
      return 0;
    }
    if (index >= total) {
      return total - 1;
    }
    return index;
  }

  private pickLocalizedValue(english: string, dutch: string, french: string): string {
    const value = this.pickByLanguage(this.language(), english, dutch, french).trim();
    if (value !== '') {
      return value;
    }

    return english || dutch || french || '-';
  }

  private pickByLanguage(language: UILanguage, english: string, dutch: string, french: string): string {
    if (language === 'nl') {
      return dutch || english || french;
    }
    if (language === 'fr') {
      return french || english || dutch;
    }
    return english || dutch || french;
  }
}
